// ═══════════════════════════════════════════════════════════════════════════
//  CHART CONSTANTS — non-type constants shared across the module
// ═══════════════════════════════════════════════════════════════════════════
import type { InstrumentDef, Session, SessionKey } from './ChartDataTypes';

// ─── Backend ──────────────────────────────────────────────────────────────────
export const API = (import.meta as any).env?.VITE_API_URL || 'https://api.jaiswalcapital.com';
export const API_BASE = (import.meta as any).env?.VITE_API_URL || 'https://api.jaiswalcapital.com';
// LOCAL LAB (God 2026-10-26: "commando drill Local PC par hogi; AWS sirf paper aur live trading ke liye"): a Local dev build
// started with VITE_LAB_HISTORY_API=http://127.0.0.1:5101 reads candles (/history) from H: through
// Algo-Engine/tools/local_lab/lab_chart_server.py - every drill day of every index and traded option contract. Unset (the AWS
// site build) = the AWS API, unchanged.
export const HISTORY_API = (import.meta as any).env?.VITE_LAB_HISTORY_API || API;
export const QUOTE_TICK_MS = 3000;
export const RECONNECT_DELAY_MS = 8000;
export const FETCH_TIMEOUT_MS = 30000;

// ─── Timeframes ───────────────────────────────────────────────────────────────
export const TF_GROUPS: { label: string; tfs: string[] }[] = [
  { label: 'SECONDS', tfs: ['1s', '5s', '15s'] },
  { label: 'MINUTES', tfs: ['1m', '3m', '5m', '15m', '30m'] },
  { label: 'HOURS',   tfs: ['1H', '4H'] },
  { label: 'DAYS',    tfs: ['1D', '1W'] },
];
export const ALL_TFS = TF_GROUPS.flatMap(g => g.tfs);

export const TF_TO_API: Record<string, string> = {
  '1s': '1_Second', '5s': '5_Second', '15s': '15_Second',
  '1m': '1_Minute', '3m': '3_Minute', '5m': '5_Minute',
  '15m': '15_Minute', '30m': '30_Minute',
  '1H': '1_Hour', '4H': '4_Hour',
  '1D': '1_Day', '1W': '1_Week',
};

// ─── TF CANONICALISATION (God 2026-09-09) ───────────────────────────────────
// THE BUG THIS ENDS. Every table in this file is keyed '1D' / '1H' / '1W' with
// a CAPITAL letter. Callers do not all agree: the Audit-V2 deeplink, the F5 DNA
// cards and the calibration store all write the daily timeframe as lowercase
// '1d'. TF_TO_API['1d'] is undefined, and dataService did
//     const apiTf = TF_TO_API[tf] || '5_Minute';
// so a 1-DAY chart silently requested FIVE-MINUTE bars. It did not error and it
// did not look broken -- it drew a perfectly convincing chart of the wrong
// thing. Measured on 2026-09-09: God opened SILVERBEES "1d" and the browser
// asked for timeframe=5_Minute, got 388 bars covering 26-Aug..09-Sep, while the
// calibration pins it was supposed to overlay sit on DAILY closes from 2021 to
// 2026. The markers were fetched (2,836 bytes arrived), parsed, and rendered
// years outside the visible range. "No signals on SuperChart" was that.
//
// The same '1d' vs '1D' trap was already fixed twice on the Python side today
// (tara_backtest_grid._TF_ALIAS, modules/tf_lanes.canon). This is the third
// place it lives, and the last one that fails SILENTLY.
export const TF_ALIAS: Record<string, string> = {
  '1d': '1D', '1day': '1D', '1w': '1W', '1week': '1W',
  '1h': '1H', '1hr': '1H', '60m': '1H', '4h': '4H', '240m': '4H',
  '1M': '1m',            // minutes are lowercase here; '1M' is a common slip
};

/** Normalise any caller's timeframe to this module's vocabulary. */
export const canonTf = (tf: string): string => {
  const t = String(tf || '').trim();
  return TF_ALIAS[t] ?? (TF_TO_API[t] ? t : (TF_ALIAS[t.toLowerCase()] ?? t));
};

export const TF_SECONDS: Record<string, number> = {
  '1s': 1, '5s': 5, '15s': 15,
  '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800,
  '1H': 3600, '4H': 14400, '1D': 86400, '1W': 604800,
};

// ─── STRICT BACKTEST HORIZON RULE (God 2026-08-09, IMMUTABLE) ────────────────
// SINGLE SOURCE OF TRUTH for "how far back must this timeframe load", mirrored
// byte-for-byte by the backend (modules/routes_history.py TF_HORIZON_DAYS) and
// the Pine backtest grid (tara_backtest_grid.py GRID_DEPTH_DAYS). Every default
// lookback below is DERIVED from this table — never hand-written per call site,
// which is how the 1m chart ended up serving a single intraday session.
//   ADAPTIVE HISTORICAL CANDLE DEPTH (God 2026-09-08, directive 6) — replaces
//   the 2026-09-02 map, which God has now superseded by his own hand:
//     sub-minute  1s / 5s / 15s        = 2 days
//     minute      1m / 5m / 15m        = 15 days
//     macro       1H / 4H / 1D / 1W    = 3 years
//   NOTE WHAT MOVED: 15m drops from 1095 days to 15. That is a deliberate
//   reversal of the 2026-09-02 ruling, which had grouped 15m with the macro
//   set, and it is written down here rather than silently applied.
//   TWO TIMEFRAMES ARE NOT IN GOD'S LIST and are therefore NOT CHANGED:
//     '30m' — his groups run sub-minute / minute / macro and name ten of the
//             twelve TFs; 30m appears in none of them. Left at 3 years.
//     '3m'  — (THREE MINUTES, not three months) held at 90 days, the same
//             standing exception recorded on 2026-09-02.
//   Guessing a value for an unnamed timeframe is how a limit becomes folklore;
//   both await a ruling.
//   MAX-DEPTH RULING (God 2026-09-15) — supersedes the minute row above.
//     "1s 5s 15s ka all symbol ka 2 din ka candle storage rehni chahiye, aur
//      other tf kay candle system kay hisab say MAX candle SuperChart par
//      rehni chahiye."
//   Sub-minute stays at God's 2 days, unchanged. EVERY other timeframe now
//   takes the 3-year ceiling, which is this system's way of saying "all of it":
//   the backend's own note is that 3 years "serves everything that exists, not
//   3 years of bars - the horizon is a ceiling, never a promise of depth". So
//   1m / 5m / 15m rise from 15 days, and 3m from 90; 30m and the macro set were
//   already there. This RESOLVES the two standing exceptions rather than
//   guessing at them - God's instruction names no timeframe, it names the rest.
//   WHY THIS WAS COSTING REAL CANDLES: 5m and 15m hold 71 days on disk
//   (measured, /history store_days) and were serving 16. The 15m pane in God's
//   screenshot was 281 bars of an available ~1,775.
//   WHY IT DOES NOT BRING BACK THE RAM PROBLEM: the 2026-09-02 blow-up was 1s
//   at 15 days (299,567 rows / 22.9 MB per entry). 1s is NOT touched here. At
//   the store's real depth, 15m is ~1.8k bars and 5m ~5.3k - two orders of
//   magnitude smaller than the series that caused it.
export const TF_HORIZON_DAYS: Record<string, number> = {
  '1s': 2,   '5s': 2,   '15s': 2,
  '1m': 1095,  '3m': 1095,
  '5m': 1095,
  '15m': 1095, '30m': 1095,
  '1H': 1095, '4H': 1095,
  '1D': 1095, '1W': 1095,
};

/** Mandated lookback for a TF, in days. */
export const horizonDays = (tf: string): number => TF_HORIZON_DAYS[tf] ?? 365;

/** ISO yyyy-mm-dd at the start of the TF's mandated horizon. */
export const horizonFromDate = (tf: string): string =>
  new Date(Date.now() - horizonDays(tf) * 86400 * 1000).toISOString().slice(0, 10);

// ─── Pagination ───────────────────────────────────────────────────────────────
// Page width per request. Sized so a handful of pages walks the FULL horizon.
// Re-sized with the 2026-09-02 map: a page wider than its own horizon just
// makes the first request fetch the whole window and the second return empty,
// so every intraday page is now <= its horizon (1s/5s/15s/1m/5m = one page).
export const TF_PAGE_DAYS: Record<string, number> = {
  // Tracks the 2026-09-08 horizons above. A page WIDER than its own horizon
  // makes the first request fetch the whole window and the second return
  // empty, so every intraday page equals its horizon and pages in one shot.
  '1s': 2,   '5s': 2,   '15s': 2,
  '1m': 15,  '3m': 30,  '5m': 15,  '15m': 15,  '30m': 180,
  '1H': 365, '4H': 365,
  '1D': 365, '1W': 1095,
};

// TFs that can be reconstructed from 1_Minute by client-side resampling
export const RESAMPLABLE_FROM_1M = new Set(['3m', '5m', '15m', '30m', '1H', '4H']);

// Initial-load bar caps — now DERIVED from the horizon rule above, not guessed.
// NSE session = 375 min/day ≈ 21 trading days/month, so a 3-month 1m mandate is
// ~23,250 bars: the old 10,000 cap silently truncated it to ~26 trading days and
// the chart looked "intraday only". Sub-minute TFs cannot ship their whole
// horizon in one payload (15 days of 1s ≈ 340k bars) — they load the newest
// slice and page backwards into the rest.
// FIRST PAINT ONLY. This is the `limit` on the opening /history call, so it is
// what the user waits for before any candle appears. It is NOT how far the
// chart can go back — that is TF_HORIZON_DAYS, reached by paging (loadOlderPage)
// as the user scrolls, which is what the "load the newest slice and page
// backwards" note above always intended.
//
// God 2026-08-17: these were the full-horizon numbers (1s=60000, 5s=40000), and
// they cost 3-4 MB and 3-5 s PER PANE — ~19 MB on the 6-pane layout, which is
// why the chart took so long to open. Measured on NIFTY_50 5s:
//     limit=40000 -> 3,148,034 bytes / 2.96 s
//     limit=2000  ->   190,779 bytes / 0.63 s     (16x smaller, ~5x faster)
// Worse, dataService derives `hasMore = candles.length >= limit`. With a 40000
// limit no real response ever reached it, so hasMore stayed FALSE and
// scroll-back silently never armed. A screen-sized page fixes both at once.
export const MAX_INITIAL_BARS_BY_TF: Record<string, number> = {
  '1s': 2000, '5s': 2000, '15s': 2000,
  '1m': 2000, '3m': 2000, '5m': 2000, '15m': 2000, '30m': 2000,
  '1H': 2000, '4H': 1500,
  '1D': 1500, '1W': 1000,
};

// IN-MEMORY RETENTION — how many bars a pane keeps after live ticks append.
// Deliberately NOT the same number as the initial page: the live-tick paths trim
// with slice(-cap), so tying this to the (now small) first-paint page would
// throw away exactly the history the user just scrolled back to fetch. These are
// the old initial-load values, so retention behaviour is unchanged.
export const MAX_RETAINED_BARS_BY_TF: Record<string, number> = {
  // 1s raised 60k -> 170k (God 2026-08-22): 7 full sessions of 1s ≈ 157k bars
  // must survive in memory for the HITL audit; render windows by view indices
  // so the draw cost is unchanged.
  '1s': 170000, '5s': 40000, '15s': 26000,
  '1m': 26000, '3m': 10000, '5m': 21000, '15m': 14000, '30m': 8000,
  '1H': 6000, '4H': 3000,
  '1D': 4000, '1W': 2000,
};

// ─── MOBILE CHART BUDGET (KALI mobile audit M-H4, God D24 2026-10-04) ────────
// An iPhone tab is killed (jetsam) long before a desktop tab would notice: a 1s
// pane could hold 340k bars on the tick path and 680k after scroll-back. On a
// phone ONE cap is used by every path (tick, loadOlder, backfill, resync,
// first load) - no x2 / x4 multipliers. Every TF stays selectable (RULE #68):
// this limits how far back a phone pane keeps bars, never which TF it can show.
export const MOBILE_CANDLE_BUDGET_BY_TF: Record<string, number> = {
  '1s': 20000,    // ~5.5 h of 1s bars
  '5s': 12000,
  '15s': 8000,
  '1m': 6000,
  '3m': 6000,
};
export const MOBILE_CANDLE_BUDGET_DEFAULT = 4000;   // 5m and above

/** Phone-class viewport for non-React code (same LEGACY 768 line as useIsMobile). */
export const isPhoneViewport = (): boolean => {
  try { return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches; }
  catch { return false; }
};

/** The one retention cap. Desktop: retain table x mult (unchanged behaviour).
 *  Phone: the mobile budget, whatever the path asked for. */
export const candleCap = (tf: string, mult: number): number => {
  if (isPhoneViewport()) return MOBILE_CANDLE_BUDGET_BY_TF[tf] ?? MOBILE_CANDLE_BUDGET_DEFAULT;
  return (MAX_RETAINED_BARS_BY_TF[tf] ?? 600) * mult;
};

/** Phone DPR cap: a DPR-3 iPhone canvas is 2.25x the pixels of DPR 2 for no
 *  visible gain on a chart; desktop keeps the true ratio. */
export const MOBILE_DPR_CAP = 2;
export const chartDpr = (): number => {
  const d = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  return isPhoneViewport() ? Math.min(d, MOBILE_DPR_CAP) : d;
};

/** Size a canvas backing store ONLY when the size/DPR actually changed.
 *  Writing cv.width reallocates the whole backing store (7-8 MB per canvas on a
 *  DPR-3 phone) and used to run on every draw. When the size is unchanged the
 *  context is reset to defaults instead, so a draw starts from the same blank
 *  state a reallocation gave it. Returns the 2D context with the DPR transform. */
export const prepCanvas = (cv: HTMLCanvasElement, w: number, h: number, dpr: number): CanvasRenderingContext2D => {
  const W = Math.round(w * dpr), H = Math.round(h * dpr);
  const ctx = cv.getContext('2d')!;
  if (cv.width !== W || cv.height !== H) {
    cv.width = W; cv.height = H;                 // realloc: state is fresh by definition
    cv.style.width = w + 'px'; cv.style.height = h + 'px';
  } else {
    const c: any = ctx;
    if (typeof c.reset === 'function') c.reset();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    ctx.setLineDash([]); ctx.lineDashOffset = 0;
    ctx.lineWidth = 1; ctx.lineCap = 'butt'; ctx.lineJoin = 'miter'; ctx.miterLimit = 10;
    ctx.fillStyle = '#000'; ctx.strokeStyle = '#000';
    ctx.shadowBlur = 0; ctx.shadowColor = 'rgba(0,0,0,0)'; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    ctx.font = '10px sans-serif'; ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
    ctx.imageSmoothingEnabled = true;
    if ('filter' in c) c.filter = 'none';
    ctx.clearRect(0, 0, W, H);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
};

/** Release a hidden canvas's backing store (display:none pane). */
export const releaseCanvas = (cv: HTMLCanvasElement | null) => {
  if (cv && (cv.width || cv.height)) { cv.width = 0; cv.height = 0; }
};

// ─── Market Sessions ──────────────────────────────────────────────────────────
export const MARKET_SESSIONS: Record<SessionKey, Session> = {
  NSE:      { open: 9 * 60 + 15, close: 15 * 60 + 30 },
  BSE:      { open: 9 * 60 + 15, close: 15 * 60 + 30 },
  MCX:      { open: 9 * 60 + 0,  close: 23 * 60 + 30 },
  MCX_AGRI: { open: 9 * 60 + 0,  close: 21 * 60 + 0  },
};
export const MCX_AGRI_SYMBOLS = new Set(['MENTHAOIL', 'COTTON', 'KAPAS', 'CASTORSEED']);
export const MCX_COMMODITY_SYMBOLS = new Set([
  'CRUDEOIL', 'NATURALGAS', 'GOLD', 'SILVER', 'COPPER', 'ZINC', 'ALUMINIUM', 'LEAD', 'NICKEL',
  // mini / micro / variant contracts (same MCX session 9:00–23:30 IST)
  'GOLDM', 'GOLDMINI', 'GOLDGUINEA', 'GOLDPETAL', 'SILVERM', 'SILVERMIC', 'SILVERMINI',
  'CRUDEOILM', 'NATURALGASM', 'NATGASMINI', 'ZINCMINI', 'ALUMINI', 'LEADMINI', 'COPPERM',
]);
export const BSE_INDEX_SYMBOLS     = new Set(['SENSEX', 'BANKEX']);
export const NSE_INDEX_SYMBOLS     = new Set(['NIFTY_50', 'BANK_NIFTY', 'FIN_NIFTY', 'MIDCPNIFTY', 'INDIA_VIX']);

// ─── Instrument Registry ─────────────────────────────────────────────────────
export const SCREENER_INSTRUMENTS: Record<string, InstrumentDef[]> = {
  'INDEX': [
    { name: 'NIFTY 50',   apiSym: 'NIFTY_50',   exch: 'NSE', type: 'INDEX' },
    { name: 'BANK NIFTY', apiSym: 'BANK_NIFTY', exch: 'NSE', type: 'INDEX' },
    { name: 'FIN NIFTY',  apiSym: 'FIN_NIFTY',  exch: 'NSE', type: 'INDEX' },
    { name: 'MIDCPNIFTY', apiSym: 'MIDCPNIFTY', exch: 'NSE', type: 'INDEX' },
    { name: 'SENSEX',     apiSym: 'SENSEX',      exch: 'BSE', type: 'INDEX' },
    { name: 'BANKEX',     apiSym: 'BANKEX',      exch: 'BSE', type: 'INDEX' },
  ],
  'FUTURES': [
    { name: 'NIFTY FUT',      apiSym: 'NIFTY_FUT',      exch: 'NSE F&O', type: 'FUT' },
    { name: 'BANKNIFTY FUT',  apiSym: 'BANKNIFTY_FUT',  exch: 'NSE F&O', type: 'FUT' },
    { name: 'FINNIFTY FUT',   apiSym: 'FINNIFTY_FUT',   exch: 'NSE F&O', type: 'FUT' },
    { name: 'SENSEX FUT',     apiSym: 'SENSEX_FUT',     exch: 'BSE F&O', type: 'FUT' },
    { name: 'RELIANCE FUT',   apiSym: 'RELIANCE_FUT',   exch: 'NSE F&O', type: 'FUT' },
    { name: 'HDFCBANK FUT',   apiSym: 'HDFCBANK_FUT',   exch: 'NSE F&O', type: 'FUT' },
    { name: 'ICICIBANK FUT',  apiSym: 'ICICIBANK_FUT',  exch: 'NSE F&O', type: 'FUT' },
    { name: 'INFY FUT',       apiSym: 'INFY_FUT',       exch: 'NSE F&O', type: 'FUT' },
    { name: 'TCS FUT',        apiSym: 'TCS_FUT',        exch: 'NSE F&O', type: 'FUT' },
    { name: 'SBIN FUT',       apiSym: 'SBIN_FUT',       exch: 'NSE F&O', type: 'FUT' },
    { name: 'AXISBANK FUT',   apiSym: 'AXISBANK_FUT',   exch: 'NSE F&O', type: 'FUT' },
    { name: 'KOTAKBANK FUT',  apiSym: 'KOTAKBANK_FUT',  exch: 'NSE F&O', type: 'FUT' },
    { name: 'BAJFINANCE FUT', apiSym: 'BAJFINANCE_FUT', exch: 'NSE F&O', type: 'FUT' },
    { name: 'BHARTIARTL FUT', apiSym: 'BHARTIARTL_FUT', exch: 'NSE F&O', type: 'FUT' },
    { name: 'LT FUT',         apiSym: 'LT_FUT',         exch: 'NSE F&O', type: 'FUT' },
    { name: 'ITC FUT',        apiSym: 'ITC_FUT',        exch: 'NSE F&O', type: 'FUT' },
    { name: 'WIPRO FUT',      apiSym: 'WIPRO_FUT',      exch: 'NSE F&O', type: 'FUT' },
    { name: 'TATAMOTORS FUT', apiSym: 'TATAMOTORS_FUT', exch: 'NSE F&O', type: 'FUT' },
    { name: 'TATASTEEL FUT',  apiSym: 'TATASTEEL_FUT',  exch: 'NSE F&O', type: 'FUT' },
    { name: 'MARUTI FUT',     apiSym: 'MARUTI_FUT',     exch: 'NSE F&O', type: 'FUT' },
    { name: 'SUNPHARMA FUT',  apiSym: 'SUNPHARMA_FUT',  exch: 'NSE F&O', type: 'FUT' },
    { name: 'HCLTECH FUT',    apiSym: 'HCLTECH_FUT',    exch: 'NSE F&O', type: 'FUT' },
    { name: 'ONGC FUT',       apiSym: 'ONGC_FUT',       exch: 'NSE F&O', type: 'FUT' },
    { name: 'ADANIPORTS FUT', apiSym: 'ADANIPORTS_FUT', exch: 'NSE F&O', type: 'FUT' },
  ],
  'NIFTY 50 STOCKS': [
    { name: 'ADANIENT',   apiSym: 'ADANIENT',   exch: 'NSE', type: 'EQ' },
    { name: 'ADANIPORTS', apiSym: 'ADANIPORTS', exch: 'NSE', type: 'EQ' },
    { name: 'APOLLOHOSP', apiSym: 'APOLLOHOSP', exch: 'NSE', type: 'EQ' },
    { name: 'ASIANPAINT', apiSym: 'ASIANPAINT', exch: 'NSE', type: 'EQ' },
    { name: 'AXISBANK',   apiSym: 'AXISBANK',   exch: 'NSE', type: 'EQ' },
    { name: 'BAJAJ-AUTO', apiSym: 'BAJAJ_AUTO', exch: 'NSE', type: 'EQ' },
    { name: 'BAJAJFINSV', apiSym: 'BAJAJFINSV', exch: 'NSE', type: 'EQ' },
    { name: 'BAJFINANCE', apiSym: 'BAJFINANCE', exch: 'NSE', type: 'EQ' },
    { name: 'BHARTIARTL', apiSym: 'BHARTIARTL', exch: 'NSE', type: 'EQ' },
    { name: 'BPCL',       apiSym: 'BPCL',       exch: 'NSE', type: 'EQ' },
    { name: 'BRITANNIA',  apiSym: 'BRITANNIA',  exch: 'NSE', type: 'EQ' },
    { name: 'CIPLA',      apiSym: 'CIPLA',      exch: 'NSE', type: 'EQ' },
    { name: 'COALINDIA',  apiSym: 'COALINDIA',  exch: 'NSE', type: 'EQ' },
    { name: 'DIVISLAB',   apiSym: 'DIVISLAB',   exch: 'NSE', type: 'EQ' },
    { name: 'DRREDDY',    apiSym: 'DRREDDY',    exch: 'NSE', type: 'EQ' },
    { name: 'EICHERMOT',  apiSym: 'EICHERMOT',  exch: 'NSE', type: 'EQ' },
    { name: 'GRASIM',     apiSym: 'GRASIM',     exch: 'NSE', type: 'EQ' },
    { name: 'HCLTECH',    apiSym: 'HCLTECH',    exch: 'NSE', type: 'EQ' },
    { name: 'HDFCBANK',   apiSym: 'HDFCBANK',   exch: 'NSE', type: 'EQ' },
    { name: 'HDFCLIFE',   apiSym: 'HDFCLIFE',   exch: 'NSE', type: 'EQ' },
    { name: 'HEROMOTOCO', apiSym: 'HEROMOTOCO', exch: 'NSE', type: 'EQ' },
    { name: 'HINDALCO',   apiSym: 'HINDALCO',   exch: 'NSE', type: 'EQ' },
    { name: 'HINDUNILVR', apiSym: 'HINDUNILVR', exch: 'NSE', type: 'EQ' },
    { name: 'ICICIBANK',  apiSym: 'ICICIBANK',  exch: 'NSE', type: 'EQ' },
    { name: 'INDUSINDBK', apiSym: 'INDUSINDBK', exch: 'NSE', type: 'EQ' },
    { name: 'INFY',       apiSym: 'INFY',       exch: 'NSE', type: 'EQ' },
    { name: 'ITC',        apiSym: 'ITC',        exch: 'NSE', type: 'EQ' },
    { name: 'JIOFIN',     apiSym: 'JIOFIN',     exch: 'NSE', type: 'EQ' },
    { name: 'JSWSTEEL',   apiSym: 'JSWSTEEL',   exch: 'NSE', type: 'EQ' },
    { name: 'KOTAKBANK',  apiSym: 'KOTAKBANK',  exch: 'NSE', type: 'EQ' },
    { name: 'LT',         apiSym: 'LT',         exch: 'NSE', type: 'EQ' },
    { name: 'LTIM',       apiSym: 'LTIM',       exch: 'NSE', type: 'EQ' },
    { name: 'M&M',        apiSym: 'M_M',        exch: 'NSE', type: 'EQ' },
    { name: 'MARUTI',     apiSym: 'MARUTI',     exch: 'NSE', type: 'EQ' },
    { name: 'NESTLEIND',  apiSym: 'NESTLEIND',  exch: 'NSE', type: 'EQ' },
    { name: 'NTPC',       apiSym: 'NTPC',       exch: 'NSE', type: 'EQ' },
    { name: 'ONGC',       apiSym: 'ONGC',       exch: 'NSE', type: 'EQ' },
    { name: 'POWERGRID',  apiSym: 'POWERGRID',  exch: 'NSE', type: 'EQ' },
    { name: 'RELIANCE',   apiSym: 'RELIANCE',   exch: 'NSE', type: 'EQ' },
    { name: 'SBIN',       apiSym: 'SBIN',       exch: 'NSE', type: 'EQ' },
    { name: 'SBILIFE',    apiSym: 'SBILIFE',    exch: 'NSE', type: 'EQ' },
    { name: 'SUNPHARMA',  apiSym: 'SUNPHARMA',  exch: 'NSE', type: 'EQ' },
    { name: 'TATACONSUM', apiSym: 'TATACONSUM', exch: 'NSE', type: 'EQ' },
    { name: 'TATAMOTORS', apiSym: 'TATAMOTORS', exch: 'NSE', type: 'EQ' },
    { name: 'TATASTEEL',  apiSym: 'TATASTEEL',  exch: 'NSE', type: 'EQ' },
    { name: 'TCS',        apiSym: 'TCS',        exch: 'NSE', type: 'EQ' },
    { name: 'TECHM',      apiSym: 'TECHM',      exch: 'NSE', type: 'EQ' },
    { name: 'TITAN',      apiSym: 'TITAN',      exch: 'NSE', type: 'EQ' },
    { name: 'ULTRACEMCO', apiSym: 'ULTRACEMCO', exch: 'NSE', type: 'EQ' },
    { name: 'UPL',        apiSym: 'UPL',        exch: 'NSE', type: 'EQ' },
    { name: 'WIPRO',      apiSym: 'WIPRO',      exch: 'NSE', type: 'EQ' },
  ],
  'ETF': [
    { name: 'NIFTYBEES',  apiSym: 'NIFTYBEES',  exch: 'NSE', type: 'ETF' },
    { name: 'BANKBEES',   apiSym: 'BANKBEES',   exch: 'NSE', type: 'ETF' },
    { name: 'GOLDBEES',   apiSym: 'GOLDBEES',   exch: 'NSE', type: 'ETF' },
    { name: 'SILVERBEES', apiSym: 'SILVERBEES', exch: 'NSE', type: 'ETF' },
    // Canonical ETF universe (user spec 2026-06-12) — Dhan scrip-master verified
    { name: 'SETFNIF50',  apiSym: 'SETFNIF50',  exch: 'NSE', type: 'ETF' },   // SBI Nifty 50
    { name: 'SETFNIFBK',  apiSym: 'SETFNIFBK',  exch: 'NSE', type: 'ETF' },   // SBI Nifty Bank
    { name: 'SETFGOLD',   apiSym: 'SETFGOLD',   exch: 'NSE', type: 'ETF' },   // SBI Gold
    { name: 'SBISILVER',  apiSym: 'SBISILVER',  exch: 'NSE', type: 'ETF' },   // SBI Silver
    { name: 'NEXT30ADD',  apiSym: 'NEXT30ADD',  exch: 'NSE', type: 'ETF' },   // DSP Sensex Next 30
    { name: 'BSE500IETF', apiSym: 'BSE500IETF', exch: 'NSE', type: 'ETF' },   // ICICI BSE 500
    { name: 'HDFCNIF100', apiSym: 'HDFCNIF100', exch: 'NSE', type: 'ETF' },   // HDFC Nifty 100
    { name: 'LICNETFSEN', apiSym: 'LICNETFSEN', exch: 'NSE', type: 'ETF' },   // LIC Sensex
    { name: 'HNGSNGBEES', apiSym: 'HNGSNGBEES', exch: 'NSE', type: 'ETF' },   // Nippon Hang Seng
    { name: 'FMCGIETF',   apiSym: 'FMCGIETF',   exch: 'NSE', type: 'ETF' },   // ICICI Nifty FMCG
  ],
  'COMMODITY': [
    { name: 'GOLD',        apiSym: 'GOLD',       exch: 'MCX', type: 'COM' },
    { name: 'GOLDM',       apiSym: 'GOLDM',      exch: 'MCX', type: 'COM' },
    { name: 'SILVER',      apiSym: 'SILVER',     exch: 'MCX', type: 'COM' },
    { name: 'SILVERM',     apiSym: 'SILVERM',    exch: 'MCX', type: 'COM' },
    { name: 'CRUDE OIL',   apiSym: 'CRUDEOIL',   exch: 'MCX', type: 'COM' },
    { name: 'CRUDEOIL M',  apiSym: 'CRUDEOILM',  exch: 'MCX', type: 'COM' },
    { name: 'NATURAL GAS', apiSym: 'NATURALGAS', exch: 'MCX', type: 'COM' },
    { name: 'COPPER',      apiSym: 'COPPER',     exch: 'MCX', type: 'COM' },
    { name: 'SILVER MIC',  apiSym: 'SILVERMIC',  exch: 'MCX', type: 'COM' },
  ],
};

export const SYMBOL_REGISTRY: Record<string, InstrumentDef> = {};
Object.values(SCREENER_INSTRUMENTS).flat().forEach(inst => { SYMBOL_REGISTRY[inst.name] = inst; });

export const UNSUPPORTED_QUOTES = new Set(['MIDCPNIFTY']);

// ─── Canvas Layout ────────────────────────────────────────────────────────────
export const PRICE_AXIS_W = 65;
export const TIME_AXIS_H  = 26;
export const SUB_PANE_HEIGHT = 80;
